import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar } from '@/components/ui/calendar';
import { Check, MapPin, Car, Clock, CreditCard, ChevronRight, ChevronLeft, ChevronDown, User, LogIn, Sparkles, Plus, Info, ArrowLeft, Gift, Star, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'
];

// Popular car makes and models in South Africa
const carMakesModels = {
  'Toyota': ['Corolla', 'Hilux', 'Fortuner', 'RAV4', 'Land Cruiser', 'Yaris', 'Camry', 'Starlet', 'Urban Cruiser', 'Prado'],
  'Volkswagen': ['Polo', 'Golf', 'Tiguan', 'T-Cross', 'Amarok', 'Touareg', 'Polo Vivo', 'T-Roc', 'Caddy', 'Transporter'],
  'Ford': ['Ranger', 'EcoSport', 'Everest', 'Figo', 'Mustang', 'Focus', 'Fiesta', 'Territory', 'Puma', 'Bronco'],
  'BMW': ['3 Series', '1 Series', 'X3', 'X5', '5 Series', 'X1', '2 Series', 'X7', 'M3', 'M4', 'iX3', '7 Series'],
  'Mercedes-Benz': ['C-Class', 'A-Class', 'GLC', 'E-Class', 'GLA', 'CLA', 'GLE', 'S-Class', 'AMG GT', 'G-Class'],
  'Audi': ['A3', 'A4', 'Q3', 'Q5', 'A1', 'Q7', 'A5', 'RS3', 'Q2', 'e-tron'],
  'Hyundai': ['Tucson', 'i20', 'Creta', 'Grand i10', 'Venue', 'Santa Fe', 'Kona', 'i30', 'Palisade', 'Staria'],
  'Kia': ['Seltos', 'Sportage', 'Picanto', 'Rio', 'Sorento', 'Carnival', 'Sonet', 'Pegas', 'Stinger', 'EV6'],
  'Nissan': ['Navara', 'X-Trail', 'Qashqai', 'Magnite', 'Patrol', 'Almera', 'NP200', 'NP300', 'Micra', 'Leaf'],
  'Mazda': ['CX-5', 'CX-3', 'Mazda3', 'CX-30', 'Mazda2', 'CX-9', 'MX-5', 'BT-50', 'CX-8', 'Mazda6'],
  'Suzuki': ['Swift', 'Vitara', 'Jimny', 'Baleno', 'S-Presso', 'Dzire', 'Ertiga', 'XL6', 'Ignis', 'Grand Vitara'],
  'Honda': ['Fit', 'HR-V', 'CR-V', 'Civic', 'Ballade', 'Jazz', 'Accord', 'BR-V', 'City', 'WR-V'],
  'Isuzu': ['D-Max', 'MU-X', 'KB Series'],
  'Jeep': ['Wrangler', 'Grand Cherokee', 'Compass', 'Renegade', 'Gladiator', 'Cherokee'],
  'Land Rover': ['Defender', 'Discovery', 'Range Rover', 'Range Rover Sport', 'Discovery Sport', 'Evoque', 'Velar'],
  'Porsche': ['Cayenne', 'Macan', '911', 'Panamera', 'Taycan', 'Boxster', 'Cayman'],
  'Volvo': ['XC40', 'XC60', 'XC90', 'S60', 'V40', 'S90', 'C40'],
  'Lexus': ['NX', 'RX', 'ES', 'UX', 'IS', 'LX', 'LC'],
  'Renault': ['Kwid', 'Duster', 'Triber', 'Kiger', 'Captur', 'Clio', 'Megane'],
  'Haval': ['Jolion', 'H6', 'H9', 'F7', 'Big Dog'],
  'GWM': ['P-Series', 'Steed', 'C30'],
  'Chery': ['Tiggo 4 Pro', 'Tiggo 7 Pro', 'Tiggo 8 Pro'],
  'Other': []
};

const carColours = [
  'White', 'Black', 'Silver', 'Grey', 'Blue', 'Red', 'Brown', 'Green', 
  'Orange', 'Yellow', 'Gold', 'Beige', 'Maroon', 'Navy', 'Other'
];

const steps = [
  { id: 1, name: 'Vehicle', icon: Car },
  { id: 2, name: 'Location', icon: MapPin },
  { id: 3, name: 'Schedule', icon: Clock },
  { id: 4, name: 'Details', icon: User },
  { id: 5, name: 'Summary', icon: CreditCard },
];

// Tier badge colors
const tierColors = {
  essential: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Essential' },
  popular: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Most Popular' },
  premium: { bg: 'bg-[#C41E3A]/10', text: 'text-[#C41E3A]', label: 'Premium' }
};

export default function Booking() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPackage = searchParams.get('package');
  const preselectedVehicle = searchParams.get('vehicle');
  const preselectedCategory = searchParams.get('category') || 'standard';
  const { user, loading: authLoading, login } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [packages, setPackages] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState({});
  const [vehicleCategories, setVehicleCategories] = useState({});
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [priceInfo, setPriceInfo] = useState(null);
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [useLoyaltyReward, setUseLoyaltyReward] = useState(false);
  
  const [formData, setFormData] = useState({
    // Vehicle info
    vehicle_type: preselectedVehicle || '',
    vehicle_category: preselectedCategory,
    vehicle_make: '',
    vehicle_model: '',
    vehicle_colour: '',
    // Package
    package_id: preselectedPackage || '',
    add_ons: [],
    // Location
    service_location: 'shop',
    address: '',
    // Schedule
    preferred_date: '',
    preferred_time: '',
    // Customer
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  // Pre-fill customer details and fetch loyalty status when user is logged in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_email: user.email || prev.customer_email
      }));
      fetchLoyaltyStatus();
    }
  }, [user]);

  useEffect(() => {
    if (formData.package_id) {
      calculatePrice();
    }
  }, [formData.package_id, formData.add_ons, useLoyaltyReward]);

  // Set vehicle category from preselected package
  useEffect(() => {
    if (preselectedPackage && packages.length > 0) {
      const pkg = packages.find(p => p.id === preselectedPackage);
      if (pkg) {
        setFormData(prev => ({
          ...prev,
          vehicle_category: pkg.vehicle_category
        }));
      }
    }
  }, [preselectedPackage, packages]);

  const fetchLoyaltyStatus = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/status`, { withCredentials: true });
      setLoyaltyStatus(response.data);
    } catch (error) {
      console.error('Error fetching loyalty status:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const [packagesRes, addonsRes, vehicleTypesRes] = await Promise.all([
        axios.get(`${API}/services/packages`),
        axios.get(`${API}/services/addons`),
        axios.get(`${API}/services/vehicle-types`)
      ]);
      setPackages(packagesRes.data);
      setAddons(addonsRes.data);
      const types = vehicleTypesRes.data.types || {};
      setVehicleTypes(types);
      setVehicleCategories(vehicleTypesRes.data.categories || {});
      
      // If vehicle is preselected, set the category
      if (preselectedVehicle && types[preselectedVehicle]) {
        setFormData(prev => ({
          ...prev,
          vehicle_category: types[preselectedVehicle].category
        }));
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    try {
      const response = await axios.post(`${API}/calculate-price`, {
        package_id: formData.package_id,
        add_ons: formData.add_ons,
        vehicle_type: formData.vehicle_type,
        service_location: formData.service_location,
        preferred_date: formData.preferred_date,
        use_loyalty_reward: useLoyaltyReward
      }, { withCredentials: true });
      setPriceInfo(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAddon = (addonId) => {
    setFormData(prev => ({
      ...prev,
      add_ons: prev.add_ons.includes(addonId)
        ? prev.add_ons.filter(id => id !== addonId)
        : [...prev.add_ons, addonId]
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: // Vehicle & Add-ons
        return formData.vehicle_type && formData.vehicle_make && formData.vehicle_model && formData.package_id;
      case 2: // Location
        return formData.service_location === 'shop';
      case 3: // Schedule
        return formData.preferred_date && formData.preferred_time;
      case 4: // Details
        return formData.customer_name && formData.customer_email && formData.customer_phone;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const bookingPayload = {
        ...formData,
        use_loyalty_reward: useLoyaltyReward,
        payment_method: 'pay_on_arrival' // Mark as pay on arrival
      };
      const bookingResponse = await axios.post(`${API}/bookings`, bookingPayload, { withCredentials: true });
      const booking = bookingResponse.data;
      
      // If total price is 0 (free loyalty reward), show success message
      if (booking.total_price === 0) {
        toast.success('Your FREE loyalty reward has been applied!');
      } else {
        toast.success('Booking confirmed! Payment will be collected on arrival.');
      }
      
      // Navigate to booking success page
      navigate('/booking/success', { 
        state: { 
          booking_id: booking.id,
          reference: booking.reference_number,
          pay_on_arrival: true
        } 
      });
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking.');
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (canProceed()) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Get vehicle types for current category
  const currentCategoryTypes = Object.entries(vehicleTypes).filter(
    ([_, info]) => info.category === formData.vehicle_category
  );

  const selectedPackage = packages.find(p => p.id === formData.package_id);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center pt-20">
        <div className="w-6 h-6 border-2 border-[#C41E3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Require login to make a booking
  if (!user) {
    return (
      <main data-testid="booking-login-required" className="bg-white min-h-screen pt-20">
        <div className="max-w-[480px] mx-auto px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-20 h-20 rounded-full bg-[#C41E3A]/10 flex items-center justify-center mx-auto mb-6">
              <User className="w-10 h-10 text-[#C41E3A]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold text-[#1d1d1f] tracking-tight mb-3">
              Sign in to book
            </h1>
            <p className="text-base text-[#86868b] mb-8 max-w-sm mx-auto">
              Please sign in or create an account to make a booking.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to={`/login?redirect=/booking${encodeURIComponent(window.location.search)}`}
                className="inline-flex items-center justify-center gap-2 bg-[#C41E3A] text-white px-8 py-3 rounded-xl font-medium hover:bg-[#a01830] transition-colors"
                data-testid="login-to-book-btn"
              >
                <LogIn className="w-5 h-5" />
                Login
              </Link>
              <Link
                to={`/signup?redirect=/booking${encodeURIComponent(window.location.search)}`}
                className="inline-flex items-center justify-center gap-2 border-2 border-[#C41E3A] text-[#C41E3A] px-8 py-3 rounded-xl font-medium hover:bg-[#C41E3A]/5 transition-colors"
                data-testid="signup-to-book-btn"
              >
                Sign Up
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
    );
  }

  // If no package is preselected, allow selection in-form instead of redirecting
  const showPackageSelector = !formData.package_id;

  return (
    <main data-testid="booking-page" className="bg-white min-h-screen pt-20">
      <div className="max-w-[800px] mx-auto px-6 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {selectedPackage && (
            <div className="inline-flex items-center gap-2 bg-[#f5f5f7] px-4 py-2 rounded-full mb-4">
              <Sparkles className="w-4 h-4 text-[#C41E3A]" />
              <span className="text-sm font-medium text-[#1d1d1f]">{selectedPackage.name}</span>
              <span className="text-sm font-bold text-[#C41E3A]">R{selectedPackage.base_price.toLocaleString()}</span>
            </div>
          )}
          <h1 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-2">
            {showPackageSelector ? 'Book Your Service' : 'Complete Your Booking'}
          </h1>
          <p className="text-sm text-[#86868b]">
            {showPackageSelector ? 'Select a package and fill in your details' : `Step ${currentStep} of ${steps.length}`}
          </p>
        </motion.div>

        {/* Package Selector - shown when no package is preselected */}
        {showPackageSelector && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <label className="block text-sm font-medium text-[#1d1d1f] mb-3">
              Select a Package <span className="text-[#C41E3A]">*</span>
            </label>
            <div className="grid gap-3">
              {packages.filter(p => p.vehicle_category === 'standard').map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => setFormData(prev => ({ ...prev, package_id: pkg.id, vehicle_category: pkg.vehicle_category }))}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    formData.package_id === pkg.id 
                      ? 'border-[#C41E3A] bg-[#C41E3A]/5' 
                      : 'border-gray-200 hover:border-[#C41E3A]/50'
                  }`}
                  data-testid={`package-select-${pkg.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-[#1d1d1f]">{pkg.name}</p>
                      <p className="text-sm text-[#86868b]">{pkg.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#C41E3A]">R{pkg.base_price.toLocaleString()}</p>
                      <p className="text-xs text-[#86868b]">{pkg.duration}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Show booking form only after package is selected */}
        {formData.package_id && (
          <>
        {/* Loyalty Program Card */}
        {loyaltyStatus && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-8 rounded-2xl p-5 border ${
              loyaltyStatus.has_reward_available 
                ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200' 
                : 'bg-[#f5f5f7] border-gray-200'
            }`}
            data-testid="loyalty-card"
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                loyaltyStatus.has_reward_available ? 'bg-amber-500' : 'bg-[#C41E3A]/10'
              }`}>
                {loyaltyStatus.has_reward_available ? (
                  <Gift className="w-6 h-6 text-white" />
                ) : (
                  <Star className="w-6 h-6 text-[#C41E3A]" />
                )}
              </div>
              <div className="flex-1">
                {loyaltyStatus.has_reward_available ? (
                  <>
                    <h3 className="font-semibold text-[#1d1d1f] mb-1">
                      You have a FREE {loyaltyStatus.reward_package.name}!
                    </h3>
                    <p className="text-sm text-[#86868b] mb-3">
                      Worth R{loyaltyStatus.reward_package.value} - Use it on this booking
                    </p>
                    <button
                      onClick={() => setUseLoyaltyReward(!useLoyaltyReward)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                        useLoyaltyReward
                          ? 'bg-amber-500 text-white'
                          : 'bg-white text-amber-600 border border-amber-300 hover:bg-amber-50'
                      }`}
                      data-testid="use-loyalty-reward"
                    >
                      {useLoyaltyReward ? '✓ Reward Applied!' : 'Apply Reward'}
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="font-semibold text-[#1d1d1f] mb-1">
                      Loyalty Program
                    </h3>
                    <p className="text-sm text-[#86868b] mb-2">
                      Complete {loyaltyStatus.bookings_until_reward} more booking{loyaltyStatus.bookings_until_reward !== 1 ? 's' : ''} to earn a FREE Full Valet!
                    </p>
                    {/* Progress bar */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#C41E3A] rounded-full transition-all"
                          style={{ width: `${(loyaltyStatus.current_progress / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-[#86868b]">
                        {loyaltyStatus.current_progress}/5
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Progress */}
        <div className="flex items-center justify-center gap-1 mb-10 overflow-x-auto pb-2">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center gap-1">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  currentStep === step.id 
                    ? 'bg-[#C41E3A] text-white' 
                    : currentStep > step.id 
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-6 h-0.5 ${currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-[#f5f5f7] rounded-2xl p-6 md:p-8">
          
          {/* Step 1: Vehicle & Add-ons (Combined) */}
          {currentStep === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="text-lg font-semibold text-[#1d1d1f] mb-6">Tell us about your vehicle</h2>
              
              {/* Vehicle Type */}
              <div className="mb-6">
                <label className="block text-xs text-[#86868b] mb-3">Vehicle Type *</label>
                <div className="flex flex-wrap gap-2">
                  {currentCategoryTypes.map(([key, info]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleInputChange('vehicle_type', key)}
                      className={`px-4 py-2 rounded-full text-sm transition-all ${
                        formData.vehicle_type === key
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-white text-[#424245] border border-gray-200 hover:border-[#C41E3A]'
                      }`}
                      data-testid={`vehicle-type-${key}`}
                    >
                      {info.label}
                    </button>
                  ))}
                </div>
                {formData.vehicle_type && vehicleTypes[formData.vehicle_type] && (
                  <p className="text-xs text-[#86868b] mt-2">
                    e.g. {vehicleTypes[formData.vehicle_type].examples}
                  </p>
                )}
              </div>

              {/* Make, Model, Colour */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Make *</label>
                  <div className="relative">
                    <select
                      value={formData.vehicle_make}
                      onChange={(e) => {
                        handleInputChange('vehicle_make', e.target.value);
                        handleInputChange('vehicle_model', ''); // Reset model when make changes
                      }}
                      className="w-full px-4 py-3 pr-10 rounded-xl border border-gray-200 bg-white focus:border-[#C41E3A] focus:outline-none text-sm appearance-none cursor-pointer"
                      data-testid="vehicle-make"
                    >
                      <option value="">Select Make</option>
                      {Object.keys(carMakesModels).map((make) => (
                        <option key={make} value={make}>{make}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Model *</label>
                  <div className="relative">
                    {formData.vehicle_make && formData.vehicle_make !== 'Other' ? (
                      <>
                        <select
                          value={formData.vehicle_model}
                          onChange={(e) => handleInputChange('vehicle_model', e.target.value)}
                          className="w-full px-4 py-3 pr-10 rounded-xl border border-gray-200 bg-white focus:border-[#C41E3A] focus:outline-none text-sm appearance-none cursor-pointer"
                          data-testid="vehicle-model-select"
                        >
                          <option value="">Select Model</option>
                          {(carMakesModels[formData.vehicle_make] || []).map((model) => (
                            <option key={model} value={model}>{model}</option>
                          ))}
                          <option value="Other">Other</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      </>
                    ) : (
                      <input
                        type="text"
                        value={formData.vehicle_model}
                        onChange={(e) => handleInputChange('vehicle_model', e.target.value)}
                        placeholder={formData.vehicle_make ? "Enter model" : "Select make first"}
                        disabled={!formData.vehicle_make}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-[#C41E3A] focus:outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        data-testid="vehicle-model-input"
                      />
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Colour</label>
                  <div className="relative">
                    <select
                      value={formData.vehicle_colour}
                      onChange={(e) => handleInputChange('vehicle_colour', e.target.value)}
                      className="w-full px-4 py-3 pr-10 rounded-xl border border-gray-200 bg-white focus:border-[#C41E3A] focus:outline-none text-sm appearance-none cursor-pointer"
                      data-testid="vehicle-colour"
                    >
                      <option value="">Select Colour</option>
                      {carColours.map((colour) => (
                        <option key={colour} value={colour}>{colour}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-300 pt-6 mt-2">
                <h3 className="text-base font-semibold text-[#1d1d1f] mb-1">Enhance Your Service</h3>
                <p className="text-xs text-[#86868b] mb-4">Add premium treatments (optional)</p>
                
                {/* Add-ons */}
                <div className="grid grid-cols-2 gap-2">
                  {addons.map((addon) => (
                    <button
                      key={addon.id}
                      type="button"
                      onClick={() => toggleAddon(addon.id)}
                      className={`p-3 rounded-xl text-left text-xs transition-all ${
                        formData.add_ons.includes(addon.id)
                          ? 'bg-white border-2 border-[#C41E3A]'
                          : 'bg-white border border-gray-200 hover:border-[#C41E3A]/50'
                      }`}
                      data-testid={`addon-${addon.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          formData.add_ons.includes(addon.id) ? 'bg-[#C41E3A]' : 'bg-gray-100'
                        }`}>
                          {formData.add_ons.includes(addon.id) ? (
                            <Check className="w-3 h-3 text-white" />
                          ) : (
                            <Plus className="w-3 h-3 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-[#1d1d1f]">{addon.name}</p>
                          <p className="text-[#86868b] text-[10px] mt-0.5 line-clamp-2">{addon.description}</p>
                          <p className="text-[#C41E3A] font-semibold mt-1">+R{addon.price.toLocaleString()}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Location */}
          {currentStep === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="text-lg font-semibold text-[#1d1d1f] mb-6">Service location</h2>
              
              <button
                type="button"
                onClick={() => handleInputChange('service_location', 'shop')}
                className={`w-full p-6 rounded-xl text-left transition-all ${
                  formData.service_location === 'shop'
                    ? 'bg-white border-2 border-[#C41E3A]'
                    : 'bg-white border border-gray-200 hover:border-[#C41E3A]/50'
                }`}
                data-testid="location-shop"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#C41E3A]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#C41E3A]" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-[#1d1d1f]">MA Africa Workshop</p>
                    <p className="text-sm text-[#86868b] mt-1">68 Serenade Road, Henville, Germiston</p>
                    <p className="text-xs text-[#C41E3A] mt-2 font-medium">Drop off your vehicle with us</p>
                  </div>
                  {formData.service_location === 'shop' && (
                    <div className="ml-auto">
                      <div className="w-6 h-6 bg-[#C41E3A] rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              </button>
            </motion.div>
          )}

          {/* Step 3: Schedule */}
          {currentStep === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="text-lg font-semibold text-[#1d1d1f] mb-6">Choose date & time</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs text-[#86868b] mb-3">Date</label>
                  <div className="bg-white rounded-xl p-3 border border-gray-200" data-testid="calendar-container">
                    <Calendar
                      mode="single"
                      selected={formData.preferred_date ? new Date(formData.preferred_date + 'T12:00:00') : undefined}
                      onSelect={(selectedDate) => {
                        if (selectedDate) {
                          const year = selectedDate.getFullYear();
                          const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                          const day = String(selectedDate.getDate()).padStart(2, '0');
                          const formatted = `${year}-${month}-${day}`;
                          setFormData(prev => ({ ...prev, preferred_date: formatted }));
                        }
                      }}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      className="mx-auto"
                      classNames={{
                        day_selected: "bg-[#C41E3A] text-white hover:bg-[#C41E3A] hover:text-white focus:bg-[#C41E3A] focus:text-white",
                        day_today: "bg-[#f5f5f7] text-[#C41E3A]",
                        day: "h-9 w-9 p-0 font-normal text-[#1d1d1f] hover:bg-[#C41E3A]/10 rounded-md",
                      }}
                      data-testid="calendar"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-3">Time</label>
                  <div className="grid grid-cols-3 gap-2">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => handleInputChange('preferred_time', time)}
                        className={`py-3 px-2 rounded-xl text-sm font-medium transition-all ${
                          formData.preferred_time === time 
                            ? 'bg-[#C41E3A] text-white' 
                            : 'bg-white text-[#424245] border border-gray-200 hover:border-[#C41E3A]'
                        }`}
                        data-testid={`time-${time}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Details */}
          {currentStep === 4 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="text-lg font-semibold text-[#1d1d1f] mb-6">Your details</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => handleInputChange('customer_name', e.target.value)}
                    placeholder="Full name"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-[#C41E3A] focus:outline-none"
                    data-testid="customer-name"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => handleInputChange('customer_email', e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-[#C41E3A] focus:outline-none"
                    data-testid="customer-email"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Phone *</label>
                  <input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => handleInputChange('customer_phone', e.target.value)}
                    placeholder="076 123 4567"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-[#C41E3A] focus:outline-none"
                    data-testid="customer-phone"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-2">Notes (optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Any special requests or vehicle condition notes?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-[#C41E3A] focus:outline-none resize-none"
                    data-testid="notes"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 5: Summary */}
          {currentStep === 5 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="text-lg font-semibold text-[#1d1d1f] mb-6">Booking Summary</h2>
              
              <div className="space-y-4">
                {/* Vehicle */}
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <p className="text-xs text-[#86868b] mb-1">Vehicle</p>
                  <p className="text-sm font-medium text-[#1d1d1f]">
                    {formData.vehicle_make} {formData.vehicle_model}
                    {formData.vehicle_colour && ` (${formData.vehicle_colour})`}
                  </p>
                  <p className="text-xs text-[#86868b]">{vehicleTypes[formData.vehicle_type]?.label}</p>
                </div>

                {/* Service */}
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <p className="text-xs text-[#86868b] mb-1">Service</p>
                  <p className="text-sm font-medium text-[#1d1d1f]">{selectedPackage?.name}</p>
                  {formData.add_ons.length > 0 && (
                    <p className="text-xs text-[#86868b] mt-1">
                      + {formData.add_ons.map(id => addons.find(a => a.id === id)?.name).join(', ')}
                    </p>
                  )}
                </div>

                {/* Date & Location */}
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <div className="flex justify-between">
                    <div>
                      <p className="text-xs text-[#86868b] mb-1">Date & Time</p>
                      <p className="text-sm font-medium text-[#1d1d1f]">{formData.preferred_date} at {formData.preferred_time}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[#86868b] mb-1">Location</p>
                      <p className="text-sm font-medium text-[#1d1d1f]">MA Africa Workshop</p>
                    </div>
                  </div>
                </div>

                {/* Price */}
                {priceInfo && (
                  <div className={`rounded-xl p-4 ${useLoyaltyReward && priceInfo.loyalty_discount > 0 ? 'bg-gradient-to-r from-amber-500 to-amber-600' : 'bg-[#C41E3A]'} text-white`}>
                    {useLoyaltyReward && priceInfo.loyalty_discount > 0 && (
                      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/20">
                        <Gift className="w-5 h-5" />
                        <span className="text-sm font-medium">Loyalty Reward Applied!</span>
                        <span className="ml-auto text-sm">-R{priceInfo.loyalty_discount.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">
                        {priceInfo.total_price === 0 ? 'FREE' : 'Total Amount'}
                      </span>
                      <span className="text-2xl font-bold">
                        {priceInfo.total_price === 0 ? 'FREE!' : `R${priceInfo.total_price.toLocaleString()}`}
                      </span>
                    </div>
                    {priceInfo.total_price > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/20">
                        <div className="flex items-center gap-2 text-amber-300 text-sm">
                          <Info className="w-4 h-4" />
                          <span>Payment on arrival at workshop</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Customer */}
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <p className="text-sm font-medium text-[#1d1d1f]">{formData.customer_name}</p>
                  <p className="text-xs text-[#86868b]">{formData.customer_email}</p>
                  <p className="text-xs text-[#86868b]">{formData.customer_phone}</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-300">
            {currentStep > 1 ? (
              <button
                onClick={prevStep}
                className="flex items-center gap-2 text-[#424245] hover:text-[#1d1d1f] font-medium"
                data-testid="prev-step"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
            ) : (
              <Link
                to="/detail"
                className="flex items-center gap-2 text-[#424245] hover:text-[#1d1d1f] font-medium"
              >
                <ChevronLeft className="w-5 h-5" />
                Change Package
              </Link>
            )}
            
            {currentStep < 5 ? (
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors ${
                  canProceed()
                    ? 'bg-[#1d1d1f] text-white hover:bg-[#2d2d2f]'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                data-testid="next-step"
              >
                Continue
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors disabled:opacity-50 ${
                  priceInfo?.total_price === 0 
                    ? 'bg-amber-500 text-white hover:bg-amber-600' 
                    : 'bg-[#C41E3A] text-white hover:bg-[#a01830]'
                }`}
                data-testid="confirm-booking"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : priceInfo?.total_price === 0 ? (
                  <>
                    <Gift className="w-5 h-5" />
                    Book Now (Free!)
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Confirm Booking
                  </>
                )}
              </button>
            )}
          </div>
        </div>
          </>
        )}
      </div>
    </main>
  );
}
